import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, BookOpen, Search } from "lucide-react";
import { listBooks, getCurrentVersion } from "../lib/bible";
import { parseReference, buildReferenceHref } from "../lib/bibleReference";
import { Input } from "../components/ui/input";
import OfflineBibleDownloader from "../components/OfflineBibleDownloader";

export default function Biblia() {
  const version = useMemo(() => getCurrentVersion(), []);
  const allBooks = useMemo(() => listBooks(), []);
  const [query, setQuery] = useState("");
  const navigate = useNavigate();

  const trimmed = query.trim();

  // Try to parse as a Bible reference ("Jo 3:16", "Salmos 23", ...)
  const parsedRef = trimmed ? parseReference(trimmed) : null;

  // Fallback: filter by book name substring (accent-insensitive, case-insensitive)
  const filterBooks = (q) => {
    if (!q) return allBooks;
    const norm = q
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase();
    return allBooks.filter((b) =>
      b.name
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .includes(norm)
    );
  };
  const filtered = parsedRef ? [] : filterBooks(trimmed);

  const antigo = filtered.filter((b) => b.testament_code === "AT");
  const novo = filtered.filter((b) => b.testament_code === "NT");

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!trimmed) return;
    if (parsedRef) {
      const href = buildReferenceHref(parsedRef);
      if (href) {
        navigate(href);
        return;
      }
    }
    // If it matched exactly one book name, go straight to it.
    if (filtered.length === 1) {
      navigate(`/biblia/${filtered[0].slug}`);
      return;
    }
    if (filtered.length === 0) {
      toast("Digite uma referência como João 3:16 ou Salmos 23.");
    }
  };

  return (
    <div className="max-w-4xl mx-auto section-padding py-8" data-testid="bible-page">
      <Link
        to="/"
        className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-navy/60 hover:text-navy mb-6"
        data-testid="bible-back"
      >
        <ArrowLeft className="w-4 h-4" /> Início
      </Link>

      <div className="mb-8 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-navy/50">Escrituras</p>
          <h1 className="font-display text-4xl sm:text-5xl text-navy mt-2">Bíblia</h1>
          <p className="text-navy/70 mt-3 text-sm" data-testid="bible-version-label">
            Versão{" "}
            <span className="font-medium text-navy">{version.name}</span>
            {version.subtitle && <span className="text-navy/55"> · {version.subtitle}</span>}
          </p>
          <p className="text-[11px] text-navy/45 mt-1">Texto em domínio público</p>
        </div>
      </div>

      <form
        onSubmit={handleSubmit}
        className="mb-4 relative"
        data-testid="bible-search"
      >
        <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-navy/40" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Buscar livro ou digitar referência (ex: João 3:16)"
          className="h-11 pl-11 bg-white border-border"
          data-testid="bible-search-input"
        />
      </form>

      {parsedRef && (
        <div
          className="mb-6 bg-white border border-gold/40 rounded-2xl p-5 flex items-center gap-4"
          data-testid="bible-reference-card"
        >
          <div className="w-11 h-11 rounded-xl bg-[hsl(var(--parchment))] flex items-center justify-center shrink-0">
            <BookOpen className="w-5 h-5 text-gold" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-[0.3em] text-gold">Referência</p>
            <p className="font-display text-lg text-navy mt-0.5">
              {(() => {
                const book = allBooks.find((b) => b.slug === parsedRef.bookSlug);
                return `${book?.name || parsedRef.bookSlug} ${parsedRef.chapter}${parsedRef.verse ? `:${parsedRef.verse}` : ""}`;
              })()}
            </p>
          </div>
          <Link
            to={buildReferenceHref(parsedRef)}
            data-testid="bible-reference-go"
            className="inline-flex items-center gap-2 bg-navy text-white hover:bg-gold hover:text-navy px-4 py-2 rounded-full text-[11px] uppercase tracking-widest transition-colors"
          >
            Abrir <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      )}

      {!parsedRef && filtered.length === 0 && trimmed && (
        <p className="text-sm text-navy/55" data-testid="bible-empty">
          Nenhum livro encontrado. Tente uma referência como{" "}
          <span className="font-medium text-navy">João 3:16</span> ou{" "}
          <span className="font-medium text-navy">Salmos 23</span>.
        </p>
      )}

      {!parsedRef && !trimmed && (
        <div className="mb-6">
          <OfflineBibleDownloader />
        </div>
      )}

      {!parsedRef && antigo.length > 0 && (
        <BookGroup label="Antigo Testamento" books={antigo} testid="bible-group-antigo" />
      )}
      {!parsedRef && novo.length > 0 && (
        <BookGroup label="Novo Testamento" books={novo} testid="bible-group-novo" />
      )}
    </div>
  );
}

function BookGroup({ label, books, testid }) {
  return (
    <section className="mb-10" data-testid={testid}>
      <p className="text-[10px] uppercase tracking-[0.3em] text-gold mb-4">{label}</p>
      <ul className="grid sm:grid-cols-2 gap-3">
        {books.map((b) => (
          <li key={b.slug}>
            <Link
              to={`/biblia/${b.slug}`}
              data-testid={`bible-book-${b.slug}`}
              className="group flex items-center gap-4 bg-white border border-border rounded-2xl p-4 card-lift"
            >
              <div className="w-11 h-11 rounded-xl bg-[hsl(var(--parchment))] flex items-center justify-center shrink-0">
                <BookOpen className="w-5 h-5 text-gold" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-display text-lg text-navy leading-tight">{b.name}</h3>
                <p className="text-xs text-navy/55 mt-0.5">
                  {b.total_chapters} {b.total_chapters === 1 ? "capítulo" : "capítulos"}
                </p>
              </div>
              <ArrowRight className="w-4 h-4 text-navy/30 group-hover:text-gold group-hover:translate-x-1 transition" />
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}
